export const SOURCES = {
  coingecko: {
    name: 'CoinGecko AI category',
    url: 'https://www.coingecko.com/en/categories/artificial-intelligence',
    type: 'Public market data',
  },
  x402: {
    name: 'Coinbase Payments and x402',
    url: 'https://www.coinbase.com/de/blog/coinbase-payments-a-complete-solution-for-stablecoin-payments',
    type: 'Provider-reported activity',
  },
  chainalysis: {
    name: 'Chainalysis 2026 Crypto Crime Report',
    url: 'https://www.chainalysis.com/blog/crypto-scams-2026/',
    type: 'Industry research',
  },
  bis: {
    name: 'BIS: AI in financial markets',
    url: 'https://www.bis.org/speeches/20260428-analysis-action-ai-financial-markets',
    type: 'Official policy evidence',
  },
  cftc: {
    name: 'CFTC: AI trading bots advisory',
    url: 'https://www.cftc.gov/sites/default/files/2024/01/AITradingBots_0.pdf',
    type: 'Official investor warning',
  },
  mica: {
    name: 'European Commission: Markets in Crypto-Assets',
    url: 'https://finance.ec.europa.eu/digital-finance/crypto-assets_en',
    type: 'Official regulation',
  },
  github: {
    name: 'GitHub Search API',
    url: 'https://docs.github.com/en/rest/search/search',
    type: 'Developer activity',
  },
  openrouter: {
    name: 'OpenRouter rankings',
    url: 'https://openrouter.ai/rankings',
    type: 'Product activity',
  },
  trends: {
    name: 'Google Trends',
    url: 'https://trends.google.com/trends/',
    type: 'Search interest',
  },
}

export const PUBLIC_SIGNALS = [
  {
    value: '160M+',
    label: 'x402 payments reported over the prior year',
    finding: 'Agent payments have moved beyond a technical demonstration.',
    catch: 'The count still needs retries, self-payments, low-value calls and repeat merchants separated.',
    source: 'x402',
    asOf: '9 Jun 2026',
  },
  {
    value: '4.5x',
    label: 'more revenue per AI-linked scam operation',
    finding: 'AI appears to improve the economics of attack, not only the volume of attempts.',
    catch: 'The sample uses visible on-chain links to AI vendors and cannot capture every AI-assisted scam.',
    source: 'chainalysis',
    asOf: '13 Jan 2026',
  },
  {
    value: '$17B',
    label: 'projected 2025 crypto scam losses',
    finding: 'The defensive side of AI and crypto has a measurable economic problem to solve.',
    catch: 'At least $14B was observed. The larger total is a projection as more illicit addresses are found.',
    source: 'chainalysis',
    asOf: '13 Jan 2026',
  },
  {
    value: 'SUPPORT',
    label: 'where finance AI adoption remains concentrated',
    finding: 'Research, monitoring and internal workflows are ahead of autonomous market decisions.',
    catch: 'This is broader finance evidence. The crypto-only reader study still has to test it.',
    source: 'bis',
    asOf: '28 Apr 2026',
  },
]

export const MARRIAGES = [
  {
    id: 'ai-for-crypto',
    short: '01',
    name: 'AI for crypto',
    claim: 'AI improves crypto decisions and defenses.',
    question: 'Does the model improve a decision after costs, errors and market regime are counted?',
    evidence: ['Research', 'Trading', 'Risk', 'Fraud', 'Compliance'],
    color: 'coral',
  },
  {
    id: 'crypto-for-ai',
    short: '02',
    name: 'Crypto for AI',
    claim: 'Crypto supplies compute, data and settlement.',
    question: 'What does the blockchain add that a normal database or payment account cannot?',
    evidence: ['Compute', 'Data', 'Provenance', 'Models', 'Payments'],
    color: 'cobalt',
  },
  {
    id: 'ai-on-crypto',
    short: '03',
    name: 'AI on crypto',
    claim: 'Agents become economic participants.',
    question: 'Is the activity autonomous, repeated and economically independent of rewards?',
    evidence: ['Wallets', 'Agents', 'Commerce', 'Coordination', 'Execution'],
    color: 'acid',
  },
]

export const METRICS = [
  {
    id: 'prompt-census',
    number: 'M01',
    name: 'Prompt Census',
    group: 'People and authority',
    state: 'Fieldwork required',
    what: 'A count of what people ask AI about crypto, split by task, country, language and investor type.',
    tells: 'Whether AI is mainly explaining the market or influencing real financial decisions.',
    check: 'A large number of prompts can still mean shallow use. Asking for an explanation is not the same as handing over a trade.',
    method: 'Share of respondents using each task in the last 30 days.',
    inputs: ['Reader poll', 'Reddit sample', 'Search Console', 'PostHog'],
    questions: ['Which prompt type is growing fastest?', 'Does use change with portfolio size?'],
  },
  {
    id: 'delegation-ratio',
    number: 'M02',
    name: 'Delegation Ratio',
    group: 'People and authority',
    state: 'Fieldwork required',
    what: 'The split between AI a person deliberately prompts and AI hidden inside a wallet, exchange or compliance workflow.',
    tells: 'Who owns the user relationship with AI: the model interface or the crypto product.',
    check: 'The biggest adoption may be invisible. Prompt counts can understate how much machine judgment already sits in the stack.',
    method: 'Explicit AI decisions divided by all AI-assisted decisions.',
    inputs: ['Reader poll', 'Exchange audit', 'App reviews', 'Expert interviews'],
    questions: ['Which platforms have the widest hidden layer?', 'Do users know when AI shaped a result?'],
  },
  {
    id: 'trust-frontier',
    number: 'M03',
    name: 'Trust Frontier',
    group: 'People and authority',
    state: 'Fieldwork required',
    what: 'The highest-risk step a person will let AI perform without approval: explain, research, screen, size, execute or hold keys.',
    tells: 'Where advice ends and genuine delegation begins.',
    check: 'Usage can rise while the trust frontier barely moves. That would favor copilots over autonomous trading products.',
    method: 'Highest independently delegated action on an ordered task ladder.',
    inputs: ['Reader poll', 'Institution interviews', 'Permission audit'],
    questions: ['Where does trust collapse?', 'Does the frontier move after a loss?'],
  },
  {
    id: 'readiness-index',
    number: 'M04',
    name: 'Readiness Index',
    group: 'People and authority',
    state: 'VPS and interviews',
    what: 'A cohort score for data quality, specialist staff, production features, evaluation and governance.',
    tells: 'Which firms can operate AI safely rather than merely announce it.',
    check: 'A public assistant is not proof of readiness. The harder work sits in data controls, audit trails and human escalation.',
    method: 'Five components shown separately before any total is published.',
    inputs: ['Feature audit', 'Job postings', 'Technical documents', 'Interviews'],
    questions: ['Who has live systems rather than pilots?', 'Which component is the bottleneck?'],
  },
  {
    id: 'prompt-outcome',
    number: 'M05',
    name: 'Prompt-to-Outcome Spread',
    group: 'Edge and economics',
    state: 'Study required',
    what: 'The result of AI-assisted decisions against a matched non-AI baseline after fees, risk and drawdowns.',
    tells: 'Whether AI improves decisions, saves time or only changes confidence.',
    check: 'Better research can reduce individual errors while making positions more crowded and fragile.',
    method: 'Risk-adjusted outcome of the AI cohort minus a matched control.',
    inputs: ['Trade diary', 'Model archive', 'Market prices', 'Fees'],
    questions: ['Does the edge survive high volatility?', 'Which task improves most?'],
  },
  {
    id: 'paid-demand',
    number: 'M06',
    name: 'Paid-Demand Ratio',
    group: 'Edge and economics',
    state: 'VPS ready',
    what: 'The share of network work paid for by users without token rewards, grants or free credits.',
    tells: 'Whether customers return because the product is useful, not because the protocol pays them to appear active.',
    check: 'Capacity, jobs and transactions can all rise while organic demand remains flat.',
    method: 'Non-incentivized paid activity divided by total activity.',
    inputs: ['Protocol jobs', 'Demand-side fees', 'Incentives', 'Repeat customers'],
    questions: ['Who still pays after rewards fall?', 'Is demand growing faster than capacity?'],
  },
  {
    id: 'subsidy-dependence',
    number: 'M07',
    name: 'Subsidy Dependence',
    group: 'Edge and economics',
    state: 'VPS ready',
    what: 'How many dollars of token emissions or incentives are spent for each dollar paid by a real customer.',
    tells: 'How expensive the network\'s apparent growth is to manufacture.',
    check: 'High use can be a liability when every new unit of activity requires more token dilution.',
    method: 'Token incentives in USD divided by demand-side fees in USD.',
    inputs: ['Emissions', 'Unlocks', 'Protocol fees', 'Treasury grants'],
    questions: ['Who survives an incentive cut?', 'Is revenue growing faster than dilution?'],
  },
  {
    id: 'value-capture',
    number: 'M08',
    name: 'Value-Capture Split',
    group: 'Edge and economics',
    state: 'VPS and disclosures',
    what: 'Where one dollar of customer spending ends up across compute, data, model, application, protocol and tokenholder.',
    tells: 'Which layer owns the economic relationship, not merely the technical workflow.',
    check: 'A blockchain can be useful while the token captures almost none of the value.',
    method: 'Layer revenue divided by total end-user spend.',
    inputs: ['Provider pricing', 'Protocol fees', 'Token burns', 'Application revenue'],
    questions: ['Who keeps the largest share?', 'Does token capture improve with scale?'],
  },
  {
    id: 'valuation-utility',
    number: 'M09',
    name: 'Valuation-to-Utility Gap',
    group: 'Market reality',
    state: 'Public and VPS',
    what: 'Token valuation set beside paid use, recurring fees and retained customers.',
    tells: 'Whether market expectations are being matched by operating evidence.',
    check: 'The loudest AI token can have the weakest link between product use and token economics.',
    method: 'FDV and annualized paid use shown side by side, never blended.',
    inputs: ['CoinGecko', 'Protocol fees', 'Paid jobs', 'Retention'],
    questions: ['Which tokens gained while paid use fell?', 'Where is utility catching up?'],
  },
  {
    id: 'without-leader',
    number: 'M10',
    name: 'Without-the-Leader Test',
    group: 'Market reality',
    state: 'Public and VPS',
    what: 'Category growth recalculated after removing the largest project, venue or customer.',
    tells: 'Whether many participants improved or one participant created the headline.',
    check: 'An expanding sector may be a single successful network wearing a category label.',
    method: 'Aggregate change minus the leader\'s contribution.',
    inputs: ['Market caps', 'Protocol jobs', 'Fees', 'Wallet activity'],
    questions: ['Does the conclusion survive without number one?', 'Is use more concentrated than value?'],
  },
  {
    id: 'agentic-settlement',
    number: 'M11',
    name: 'Agentic Settlement Quality',
    group: 'Autonomy and risk',
    state: 'VPS ready',
    what: 'Net value, unique counterparties and repeat merchants in payments initiated by software agents.',
    tells: 'Whether agents are becoming economic participants rather than producing repeated technical calls.',
    check: 'Millions of payments can still describe a narrow loop of tiny, repeated or self-funded transactions.',
    method: 'Net external value plus repeat counterparties after bot and self-pay filters.',
    inputs: ['x402 logs', 'Wallet labels', 'Stablecoin transfers', 'Merchant endpoints'],
    questions: ['How much value moves per real counterparty?', 'Which services earn repeat spend?'],
  },
  {
    id: 'threat-defense',
    number: 'M12',
    name: 'Threat-to-Defense Asymmetry',
    group: 'Autonomy and risk',
    state: 'Public and interviews',
    what: 'AI-enabled scam proceeds and attack efficiency compared with prevented losses, detection time and false alarms.',
    tells: 'Whether attackers or defenders are extracting more economic value from AI.',
    check: 'Better detection models do not guarantee safer users if attackers scale persuasion faster.',
    method: 'Attack loss and defense performance stay separate, then direction is compared.',
    inputs: ['Chainalysis', 'Exchange teams', 'Law enforcement', 'False-positive logs'],
    questions: ['Are losses growing faster than prevention?', 'Which attack gains most from AI?'],
  },
]

export const CHAPTERS = [
  {
    id: 'stack',
    roman: 'I',
    name: 'The Intelligence Stack',
    job: 'Map where AI already sits across the crypto economy.',
    question: 'Where is AI visible, where is it embedded, and who owns the user relationship?',
    feeds: ['OpenRouter', 'GitHub', 'Market baskets', 'Feature timeline'],
    charts: ['Three-marriage map', 'Model-provider share', 'Developer adoption curve', 'AI equities vs AI tokens'],
    metrics: ['Delegation Ratio', 'Readiness Index'],
  },
  {
    id: 'retail',
    roman: 'II',
    name: 'The Prompted Portfolio: Retail',
    job: 'Measure how private investors use AI before they trade.',
    question: 'What do investors ask, what will they delegate, and where does trust stop?',
    feeds: ['Reader poll', 'Google Trends', 'Reddit', 'Search Console', 'PostHog'],
    charts: ['Prompt Census', 'Trust ladder', 'Intent vs BTC', 'Language-market map'],
    metrics: ['Prompt Census', 'Trust Frontier', 'Prompt-to-Outcome Spread'],
  },
  {
    id: 'institutional',
    roman: 'III',
    name: 'The Institutional Desk',
    job: 'Separate operational AI from institutional press releases.',
    question: 'Which workflows are live, audited and consequential?',
    feeds: ['Expert interviews', 'Job postings', 'Filings', 'Vendor disclosures'],
    charts: ['Use-case maturity', 'Cohort readiness', 'Build vs buy', 'Human escalation map'],
    metrics: ['Readiness Index', 'Delegation Ratio'],
  },
  {
    id: 'platform',
    roman: 'IV',
    name: 'The Platform Race',
    job: 'Benchmark the AI features exchanges, brokers, wallets and DeFi interfaces ship.',
    question: 'Are products solving the tasks users ask for, and do users notice?',
    feeds: ['Feature audit', 'Release notes', 'App reviews', 'Traffic and use'],
    charts: ['Feature matrix', 'Launch timeline', 'Use vs awareness', 'Prompt ownership'],
    metrics: ['Delegation Ratio', 'Readiness Index'],
  },
  {
    id: 'wallet',
    roman: 'V',
    name: 'Both Sides of the Wallet',
    job: 'Put AI-enabled attacks and AI-enabled defenses on the same page.',
    question: 'Which side is improving its economics faster?',
    feeds: ['Scam attribution', 'Incident reports', 'Detection logs', 'Wallet screening'],
    charts: ['Attack vs defense', 'Loss per operation', 'Detection lead time', 'Threat taxonomy'],
    metrics: ['Threat-to-Defense Asymmetry', 'Trust Frontier'],
  },
  {
    id: 'rules',
    roman: 'VI',
    name: 'Risk, Compliance and the Two Rulebooks',
    job: 'Map AI governance onto crypto obligations without treating every system the same.',
    question: 'When does a model become a consequential financial system?',
    feeds: ['MiCA', 'EU AI guidance', 'AML rules', 'Enforcement'],
    charts: ['Obligation matrix', 'Authority ladder', 'Jurisdiction timeline', 'Governance gaps'],
    metrics: ['Readiness Index', 'Trust Frontier'],
  },
  {
    id: 'data',
    roman: 'VII',
    name: 'The Data and Compute Layer',
    job: 'Test whether crypto supplies AI with scarce, paid resources.',
    question: 'Is there durable demand for compute, data, provenance and settlement?',
    feeds: ['Network jobs', 'GPU prices', 'Protocol fees', 'Emissions', 'Agent payments'],
    charts: ['Demand vs capacity', 'Price comparison', 'Value-capture split', 'Agent settlement'],
    metrics: ['Paid-Demand Ratio', 'Subsidy Dependence', 'Value-Capture Split'],
  },
  {
    id: 'outlook',
    roman: 'VIII',
    name: 'The Outlook',
    job: 'Turn the evidence into conditional scenarios rather than a single forecast.',
    question: 'Which signals would confirm, weaken or break each AI-crypto thesis?',
    feeds: ['All chapter outputs', 'Capability benchmarks', 'Policy calendar', 'Funding and launches'],
    charts: ['Scenario tree', 'Signal watchlist', 'Evidence portfolio', 'What changed'],
    metrics: ['Valuation-to-Utility Gap', 'Without-the-Leader Test'],
  },
]

export const PROTOCOLS = [
  { name: 'Bittensor', marriage: 'Crypto for AI', role: 'Model and incentive network', proof: 'Paid demand, subnet concentration, emissions and external users' },
  { name: 'Render', marriage: 'Crypto for AI', role: 'Distributed GPU rendering and compute', proof: 'Paid jobs, utilization, repeat customers and token burn' },
  { name: 'Akash', marriage: 'Crypto for AI', role: 'Decentralized cloud marketplace', proof: 'GPU utilization, price gap and repeat deployers' },
  { name: 'io.net', marriage: 'Crypto for AI', role: 'Distributed GPU coordination', proof: 'Verified supply, paid clusters, failure rate and incentives' },
  { name: 'Vana', marriage: 'Crypto for AI', role: 'User-owned data networks', proof: 'Data buyers, contributor concentration and recurring spend' },
  { name: 'ASI Alliance', marriage: 'Crypto for AI', role: 'Agent and model coordination', proof: 'Live use, merger economics and token necessity' },
  { name: 'Virtuals', marriage: 'AI on crypto', role: 'Tokenized agent launch and coordination', proof: 'Agent revenue, repeat users and post-launch retention' },
  { name: 'x402', marriage: 'AI on crypto', role: 'Machine-readable stablecoin payments', proof: 'Net value, merchants, retries, self-payments and retention' },
  { name: 'Coinbase AgentKit', marriage: 'AI on crypto', role: 'Agent wallets and actions', proof: 'Production agents, transaction purpose and permission controls' },
  { name: 'Chainlink', marriage: 'Crypto for AI', role: 'Verifiable data and automation', proof: 'AI-specific data demand, fees and unique consumers' },
  { name: 'Exchange copilots', marriage: 'AI for crypto', role: 'Research and order assistance', proof: 'Use, trust frontier, error rate and conversion to action' },
  { name: 'AI fraud defense', marriage: 'AI for crypto', role: 'Scam and transaction screening', proof: 'Prevented loss, lead time, recall and false positives' },
]

export const DATA_FEEDS = [
  ['CoinGecko categories', 'Public', '15 min', 'Prices, market cap and constituents', 'I, VII, VIII'],
  ['OpenRouter rankings', 'Public / key', 'Weekly', 'Crypto app activity and model share', 'I, IV'],
  ['GitHub Search', 'Keyed public', 'Daily', 'Repositories, SDK use, stars and contributors', 'I, III'],
  ['Google Trends', 'Public proxy', 'Weekly', 'Retail search intent by term and country', 'II'],
  ['Reddit / Arctic Shift', 'Public / mirror', 'Monthly', 'AI discussion and prompt samples', 'II'],
  ['Search Console', 'Proprietary', 'Monthly', 'AI search demand by language market', 'II, IV'],
  ['PostHog', 'Proprietary', 'Monthly', 'AI article use, readers and scroll depth', 'II, IV'],
  ['App reviews', 'Paid / scrape', 'Monthly', 'Awareness and satisfaction with AI features', 'II, IV'],
  ['Protocol jobs and fees', 'Mixed', 'Daily', 'Paid demand, capacity, incentives and capture', 'VII, VIII'],
  ['x402 and agent payments', 'Provider / chain', 'Daily', 'Settlement value, agents and counterparties', 'VII, VIII'],
  ['Security and scam feeds', 'Mixed', 'Monthly', 'Attack loss, detection and prevention', 'V'],
  ['Regulatory corpus', 'Official', 'Weekly', 'Rules, guidance, consultations and enforcement', 'VI, VIII'],
]

export const SPLICES = {
  'stack|retail': ['Are developer integrations growing where retail intent is rising?', 'Join GitHub and OpenRouter activity to Trends, Search Console and survey adoption.', 'Developer supply vs retail demand with a lag selector.'],
  'retail|platform': ['Do platforms ship the AI features users say they want?', 'Join Prompt Census tasks to product features, launches and review mentions.', 'Demand-to-feature gap matrix by task and platform.'],
  'institutional|rules': ['Does governance rise with the consequence of the AI decision?', 'Join workflow maturity to controls, decision rights and obligations.', 'Decision authority vs governance coverage.'],
  'platform|wallet': ['Do visible safety features reduce losses or mainly reassure users?', 'Join fraud-feature launches to exposure, prevented transfers and false positives.', 'Safety launch event study.'],
  'wallet|rules': ['Where does the fastest-growing threat meet the weakest control?', 'Join threat type to jurisdiction, obligation, enforcement and platform coverage.', 'Threat-control coverage matrix.'],
  'stack|data': ['Is AI-token valuation following genuine demand for compute and data?', 'Join token value and returns to paid jobs, fees, utilization and incentives.', 'Valuation vs utility with a remove-the-leader switch.'],
  'data|outlook': ['Which infrastructure theses survive lower token incentives?', 'Join paid demand and capture to unlocks, emissions and scenarios.', 'Subsidy stress table with confirm, weaken and break thresholds.'],
  'retail|wallet': ['Does heavier AI use make investors safer or easier to persuade?', 'Join prompt behavior and scam checks to victim reports and attack types.', 'AI reliance vs scam exposure by cohort.'],
}

export const ASK_STARTERS = [
  'Where is paid demand stronger than the token story?',
  'Does 160 million agent payments prove real demand?',
  'Which metric exposes AI-token hype fastest?',
  'Where does investor trust stop?',
  'What survives when the market leader is removed?',
  'Are attackers extracting more value from AI than defenders?',
]

